// SettingsEmployeeDialog - MODULE
// ***********************************************************************************************************************
import downloadjs from "downloadjs";
import * as React from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import * as baseService from "../../services/baseService";
import * as documentService from "../../services/documentService";
import * as employeeService from "../../services/employeeService";
import * as tokenService from "../../services/tokenService";
import { Base } from "../../framework/base";
import { SaveData } from "../../framework/saveData";
import { CheckBox } from "../framework/checkbox";
import { IEmployeeEdit } from "../../models/employee/employeeEdit";
import { Button } from "../framework/button";
import { SettingsEmployeeDialogDayBookingList } from "./settingsEmployeeDialogDayBookingList";
import {
    IDocument,
    Document,
    IDocumentInfoSave,
    DocumentInfoSave,
} from "../../models/document/document";
import { DocumentList } from "../document/documentList";
import { SettingsChangePasswordDialog } from "./settingsChangePasswordDialog";
import { SettingsEmployeeDialogChangeUsernameDialog } from "./settingsEmployeeDialogChangeUsernameDialog";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import {
    EmployeeEditItemEmployeeCompetencyItem,
    IEmployeeEditItemEmployeeCompetencyItem,
    EmployeeEditItemSaveEmployeeCompetencyItem,
} from "../../models/employee/employeeEditItemEmployeeCompetencyItem";
import { EmployeeEditCompetencyItem } from "../../models/employee/employeeEditCompetencyItem";
import { SettingsEmployeeDialogEmployeeCompetencyList } from "./settingsEmployeeDialogEmployeeCompetencyList";
import {
    ScrollSpy,
    IScrollSpyItem,
    ScrollSpyItem,
} from "../framework/scrollSpy";
import { ImageSelector } from "../framework/imageSelector";
import {
    IdTitleDescription,
    IIdTitleDescription,
} from "../../models/common/idTitleDescription";
import { IVehicleItem, VehicleItem } from "../../models/vehicle/vehicleItem";
import { IDayBookingEditItem } from "../../models/dayBooking/dayBookingEditItem";
import { SettingsEmployeeDialogSsoIntegrationList } from "./settingsEmployeeDialogSsoIntegrationList";
import { IEmployeeEditItemSsoIntegrationAttribute } from "../../models/employee/employeeEditItemSsoIntegrationAttribute";
import {
    EmployeeEditItemSaveSsoIntegration,
    IEmployeeEditItemSsoIntegration,
} from "../../models/employee/employeeEditItemSsoIntegration";
import { IUserGroupItem } from "../../models/userGroup/userGroupItem";
import { SettingsEmployeeWorkHourAccountTransactions } from "./settingsEmployeeWorkHourAccountTransactions";
import { IWorkTimeAccountTransaction } from "../../models/workTimeAccount/workTimeAccountTransaction";
import { DocumentType } from "../../models/common/enums";
import { IEmployeeEditEmployeeGroupItem } from "../../models/employee/employeeEditEmployeeGroupItem";
import { SettingsEmployeeDialogEmployeeGroupList } from "./settingsEmployeeDialogEmployeeGroupList";
import { t } from "i18next";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import {
    cultureCodeEnglish,
    cultureCodeFinnish,
    cultureCodeSwedish,
} from "../../models/common/consts";
// SettingsEmployeeDialog
// ***********************************************************************************************************************
export interface ISettingsEmployeeDialogEditViewProp {
    classes?: string;
    employeeEdit: IEmployeeEdit;
    userGroups: IUserGroupItem[];
    dayBookings: IDayBookingEditItem[];
    employeeEmployeeGroups: IEmployeeEditEmployeeGroupItem[];
    ssoIntegrations: IEmployeeEditItemSsoIntegration[];
    onDayBookingRemoved: (id: string) => void;
    onAddDayBooking: (
        id: string,
        dayBookingTypeId: string,
        date: number,
        duration: number,
        dayUsageType: number,
        comment: string,
        rowId: string
    ) => void;
    onEditDayBooking: (
        id: string,
        dayBookingTypeId: string,
        date: number,
        duration: number,
        dayUsageType: number,
        comment: string,
        rowId: string
    ) => void;
    onEmployeeEmployeeGroupRemoved: (id: string) => void;
    onAddEmployeeEmployeeGroup: (
        id: string,
        employeeGroupId: string,
        groupName: string
    ) => void;
    onEditSsoIntegration: (
        serviceName: string,
        attributes: IEmployeeEditItemSsoIntegrationAttribute[]
    ) => void;
    workHourTransactions: IWorkTimeAccountTransaction[];
    workHourBalance: number;
}

export interface ISettingsEmployeeDialogEditViewState {
    vehicles: IVehicleItem[];
    costCenters: IIdTitleDescription[];
    rowId: string;
    picture: string;
    pictureFile: File;
    removePicture: boolean;
    firstName: string;
    lastName: string;
    abbreviation: string;
    phone: string;
    email: string;
    erp: string;
    employeeGroupId: string;
    salaryEmployeeGroupId: string;
    vehicleId: string;
    costCenterId: string;
    userGroupId: string;
    salaryTypeId: string;
    username: string;
    password: string;
    passwordConfirmation: string;
    activeState: number;
    workTimeAccountInUse: boolean;
    defaultCulture: string;

    competencies: IEmployeeEditItemEmployeeCompetencyItem[];
    removedEmployeeCompetencyIds: string[];
    savedEmployeeCompetencyIds: string[];

    documents: IDocument[];
    removedDocumentIds: string[];
    showChangePasswordDialog: boolean;
    showChangeUsernameDialog: boolean;

    scrollSpyItems: IScrollSpyItem[];

    postalCodeRangeStart: string;
    postalCodeRangeEnd: string;
    mapOnId: number;
    tachoCardId: string;
}

export class SettingsEmployeeDialogEditView extends React.Component<
    ISettingsEmployeeDialogEditViewProp,
    ISettingsEmployeeDialogEditViewState
> {
    private static orgStateHash: string = "";
    private employeeContentAreaDiv: HTMLDivElement;
    private static getScrollSpyItems(
        workTimeAccountInUse: boolean
    ): IScrollSpyItem[] {
        const result: IScrollSpyItem[] = [];
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "general",
                t("common.general"),
                "title"
            )
        );
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "dayBookings",
                t("dayBooking.s"),
                "title"
            )
        );
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "employeeGroups",
                t("common.secondaryEmployeeGroups"),
                "title"
            )
        );
        if (workTimeAccountInUse) {
            result.push(
                ScrollSpyItem.createScrollSpyItem(
                    "workHourTransactions",
                    t("common.workhourBank"),
                    "title"
                )
            );
        }
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "competencies",
                t("common.competencies"),
                "title"
            )
        );
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "documents",
                t("document.s"),
                "title"
            )
        );
        result.push(
            ScrollSpyItem.createScrollSpyItem(
                "integrations",
                t("common.ssoIntegrations"),
                "title"
            )
        );
        return result;
    }

    constructor(props: ISettingsEmployeeDialogEditViewProp) {
        super(props);
        const employee = props.employeeEdit.employee;
        const vehicles = props.employeeEdit.vehicles.slice(0);
        const unselectedVehicle = new VehicleItem();
        unselectedVehicle.id = Base.emptyGuid;
        vehicles.unshift(unselectedVehicle);
        const costCenters = props.employeeEdit.costCenters.slice(0);
        costCenters.unshift(
            IdTitleDescription.createIdTitleDescription(Base.emptyGuid, "", "")
        );
        this.state = {
            vehicles: vehicles,
            costCenters: costCenters,
            rowId: employee.rowId,
            firstName: employee.firstName,
            lastName: employee.lastName,
            abbreviation: employee.abbreviation,
            picture: employee.picture,
            pictureFile: null,
            removePicture: false,
            phone: employee.phone,
            email: employee.email,
            erp: employee.erp,
            employeeGroupId: employee.employeeGroupId,
            salaryEmployeeGroupId: employee.salaryEmployeeGroupId,
            vehicleId: employee.vehicleId,
            costCenterId: employee.costCenterId,
            userGroupId: employee.userGroupId,
            salaryTypeId: employee.salaryTypeId,
            username: employee.username,
            password: "",
            passwordConfirmation: "",
            activeState: employee.activeState,
            competencies:
                EmployeeEditItemEmployeeCompetencyItem.setCompetencyNames(
                    employee.competencies.map(
                        (i) => new EmployeeEditItemEmployeeCompetencyItem(i)
                    ),
                    props.employeeEdit.competencies
                ),
            removedEmployeeCompetencyIds: [],
            savedEmployeeCompetencyIds: [],
            documents: employee.documents.map((i) => new Document(i)),
            removedDocumentIds: [],
            showChangePasswordDialog: false,
            showChangeUsernameDialog: false,
            scrollSpyItems:
                SettingsEmployeeDialogEditView.getScrollSpyItems(true),
            workTimeAccountInUse: employee.workTimeAccountInUse,
            postalCodeRangeStart: employee.postalCodeRangeStart,
            postalCodeRangeEnd: employee.postalCodeRangeEnd,
            mapOnId: employee.mapOnId,
            tachoCardId: employee.tachoCardId,
            defaultCulture: employee.defaultCulture,
        };
        const saveData = SettingsEmployeeDialogEditView.getSaveDataFromState(
            props,
            this.state
        );
        SettingsEmployeeDialogEditView.orgStateHash = saveData.hash;
    }

    // #region General
    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "FNm") {
            this.setState({ firstName: value });
        } else if (name === "LNm") {
            this.setState({ lastName: value });
        } else if (name === "abbreviation") {
            this.setState({ abbreviation: value });
        } else if (name === "Phn") {
            this.setState({ phone: value });
        } else if (name === "Eml") {
            this.setState({ email: value });
        } else if (name === "vehicleId") {
            this.setState({ vehicleId: value });
        } else if (name === "costCenterId") {
            this.setState({ costCenterId: value });
        } else if (name === "userGroupId") {
            this.setState({ userGroupId: value });
        } else if (name === "salaryTypeId") {
            this.setState({ salaryTypeId: value });
        } else if (name === "uNm") {
            this.setState({ username: value });
        } else if (name === "password") {
            this.setState({ password: value });
        } else if (name === "passwordConfirmation") {
            this.setState({ passwordConfirmation: value });
        } else if (name === "erp") {
            this.setState({ erp: value });
        } else if (name === "employeeGroupId") {
            this.setState({ employeeGroupId: value });
        } else if (name === "salaryEmployeeGroupId") {
            this.setState({ salaryEmployeeGroupId: value });
        } else if (name === "PostalCodeRangeFrom") {
            this.setState({ postalCodeRangeStart: value });
        } else if (name === "PostalCodeRangeTo") {
            this.setState({ postalCodeRangeEnd: value });
        } else if (name === "maponid") {
            this.setState({ mapOnId: +value });
        } else if (name === "tachoCardId") {
            this.setState({ tachoCardId: value });
        } else if (name === "defaultCulture") {
            this.setState({ defaultCulture: value });
        }
    };

    handleActiveStateChange = (newActiveState: number) => {
        this.setState({ activeState: newActiveState });
    };

    handleWorkTimeInUseChange = (workTimeAccountInUse: boolean) => {
        this.setState({
            workTimeAccountInUse: workTimeAccountInUse,
            scrollSpyItems:
                SettingsEmployeeDialogEditView.getScrollSpyItems(
                    workTimeAccountInUse
                ),
        });
    };

    handleChangeUsername = () => {
        this.setState({
            showChangeUsernameDialog: true,
        });
    };

    handleChangePassword = () => {
        this.setState({
            showChangePasswordDialog: true,
        });
    };

    handleSetPicture = (file: File) => {
        if (Base.isNullOrUndefined(file)) return;
        const obj = this;
        Base.blobToBase64(file).then((base64Image) => {
            obj.setState({
                pictureFile: file,
                picture: base64Image,
                removePicture: false,
            });
        });
    };

    handleRemovePicture = () => {
        this.setState({
            pictureFile: null,
            picture: "",
            removePicture: true,
        });
    };
    // #endregion General

    // #region EmployeeCompetencies
    saveEmployeeCompetency = (
        competencies: IEmployeeEditItemEmployeeCompetencyItem[],
        savedEmployeeCompetencyIds: string[],
        item: EmployeeEditItemEmployeeCompetencyItem,
        id: string,
        competencyId: string,
        date: number,
        expireDate: number,
        comment: string,
        documents: IDocument[],
        removedDocumentIds: string[]
    ) => {
        item.id = id;
        item.competencyId = competencyId;
        const value = EmployeeEditCompetencyItem.getNameAndCompetencyNameById(
            this.props.employeeEdit.competencies,
            competencyId
        );
        item.competencyName = value.name;
        item.competencyTypeName = value.competencyTypeName;
        item.date = date;
        item.expireDate = expireDate;
        item.comment = comment;
        item.documents = documents;
        savedEmployeeCompetencyIds.push(item.id);
        EmployeeEditItemEmployeeCompetencyItem.sortEmployeeEditItemEmployeeCompetencyItems(
            competencies,
            "name",
            true
        );
        const allRemovedDocumentIds = this.state.removedDocumentIds.slice(0);
        for (let i = 0; i < removedDocumentIds.length; i++) {
            allRemovedDocumentIds.push(removedDocumentIds[i]);
        }
        this.setState({
            competencies: competencies,
            savedEmployeeCompetencyIds: savedEmployeeCompetencyIds,
            removedDocumentIds: removedDocumentIds,
        });
    };

    handleEmployeeCompetencyAdd = (
        id: string,
        competencyId: string,
        date: number,
        expireDate: number,
        comment: string,
        documents: IDocument[],
        removedDocumentIds: string[]
    ) => {
        const competencies = this.state.competencies.slice(0);
        const savedEmployeeCompetencyIds =
            this.state.savedEmployeeCompetencyIds.slice(0);
        const competency = new EmployeeEditItemEmployeeCompetencyItem();
        competencies.push(competency);
        this.saveEmployeeCompetency(
            competencies,
            savedEmployeeCompetencyIds,
            competency,
            id,
            competencyId,
            date,
            expireDate,
            comment,
            documents,
            removedDocumentIds
        );
    };

    handleEmployeeCompetencyEdit = (
        id: string,
        competencyId: string,
        date: number,
        expireDate: number,
        comment: string,
        documents: IDocument[],
        removedDocumentIds: string[]
    ) => {
        if (!id) return;
        const competencies = this.state.competencies.slice(0);
        const index = competencies.findIndex((i) => i.id === id);
        if (index < 0) return;
        const savedEmployeeCompetencyIds =
            this.state.savedEmployeeCompetencyIds.filter((i) => i !== id);
        this.saveEmployeeCompetency(
            competencies,
            savedEmployeeCompetencyIds,
            competencies[index],
            id,
            competencyId,
            date,
            expireDate,
            comment,
            documents,
            removedDocumentIds
        );
    };

    handleEmployeeCompetencyRemove = (id: string) => {
        if (!id) return;
        const item = this.state.competencies.find((i) => i.id === id);
        if (Base.isNullOrUndefined(item)) return;
        const removedEmployeeCompetencyIds =
            this.state.removedEmployeeCompetencyIds.filter((i) => i !== id);
        if (!item.isNew()) {
            removedEmployeeCompetencyIds.push(id);
        }
        this.setState({
            competencies: this.state.competencies.filter((i) => i.id !== id),
            savedEmployeeCompetencyIds:
                this.state.savedEmployeeCompetencyIds.filter((i) => i !== id),
            removedEmployeeCompetencyIds: removedEmployeeCompetencyIds,
        });
    };
    // #endregion EmployeeCompetencies

    // #region Documents
    handleDocumentsAdd = (fileList: FileList) => {
        const obj = this;
        const employee = this.props.employeeEdit.employee;
        Document.addFileListToDocuments(
            employee.id,
            this.state.documents,
            fileList
        ).then((documents) => {
            Document.sortDocuments(documents, "title", true);
            obj.setState({ documents: documents });
        });
    };

    handleDocumentDownload = (id: string) => {
        if (!id) return;
        const document = this.state.documents.find((i) => i.id === id);
        if (!document) return;
        if (document.file) {
            downloadjs(document.file, document.reference); //, "image/jpeg"
            return;
        }
        if (document.isNew()) return;
        documentService.getDocument(id).catch((error) => {
            store.customStore.dispatch(
                storeActions.showErrorMessage(
                    baseService.getErrorMessageFromError(error)
                )
            );
        });
    };

    handleDocumentEdit = (
        id: string,
        comment: string,
        documentType: DocumentType
    ) => {
        if (!id) return;
        const documents = this.state.documents.slice(0);
        const document = documents.find((i) => i.id === id);
        if (!document) return;
        document.comment = comment;
        document.documentType = documentType;
        this.setState({
            documents: documents,
        });
    };

    handleDocumentRemove = (id: string) => {
        if (!id) return;
        const documents = this.state.documents.filter((i) => i.id !== id);
        const removedDocumentIds = this.state.removedDocumentIds.slice(0);
        removedDocumentIds.push(id);
        this.setState({
            documents: documents,
            removedDocumentIds: removedDocumentIds,
        });
    };
    // #endregion Documents

    private static checkErrors = (
        props: ISettingsEmployeeDialogEditViewProp,
        state: ISettingsEmployeeDialogEditViewState
    ): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            const employee = props.employeeEdit.employee;
            const ownerParameters = new OwnerParameters();
            const minLength =
                ownerParameters.getOptionOwnerUsernameMinimumLength();

            if (!state.firstName) {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        t("common.firstNameMustBeDefined")
                    )
                );
                return resolve(false);
            }
            if (!state.lastName) {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        t("common.lastNameMustBeDefined")
                    )
                );
                return resolve(false);
            }
            if (employee.isNew()) {
                if (!state.username) {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            t("common.usernameMustBeDefined")
                        )
                    );
                    return resolve(false);
                }
                if (!state.password) {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            t("common.passwordMustBeDefined")
                        )
                    );
                    return resolve(false);
                }
                if (!Base.isValidUsername(state.username, minLength)) {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            t("common.usernameMustMeetComplexityRequirements", {
                                p0: minLength.toString(),
                            })
                        )
                    );
                    return resolve(false);
                }
                if (!Base.isValidPassword(state.password)) {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            t("common.passwordMustMeetComplexityRequirements")
                        )
                    );
                    return resolve(false);
                }
                if (state.password !== state.passwordConfirmation) {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            t("common.passwordConfirmationDoesNotMatchPassword")
                        )
                    );
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    private static getWarningMessage = (
        state: ISettingsEmployeeDialogEditViewState
    ): Promise<string> => {
        return new Promise<string>((resolve) => {
            //if (!state.vehicleId || state.vehicleId === Base.emptyGuid) {
            //    return resolve(t("employee.whenVehicleIsNotDefinedEmployeeCannotMakeRideBookings") + Base.lf);
            //}
            return resolve("");
        });
    };

    private static validate = (
        props: ISettingsEmployeeDialogEditViewProp,
        state: ISettingsEmployeeDialogEditViewState,
        saveCallback: () => void
    ): Promise<void> => {
        return SettingsEmployeeDialogEditView.checkErrors(props, state).then(
            (success) => {
                if (success) {
                    return SettingsEmployeeDialogEditView.getWarningMessage(
                        state
                    ).then((warnings) => {
                        if (!warnings) {
                            saveCallback();
                            return new Promise<void>((resolve) => {
                                resolve();
                            });
                        }
                        return new Promise<void>((resolve) => {
                            store.customStore.dispatch(
                                storeActions.setConfirmation(
                                    ConfirmationDialogType.Warning,
                                    t("common.warning"),
                                    warnings +
                                        Base.lf +
                                        t("common.doYouReallyWantToSaveData"),
                                    () => {
                                        store.customStore.dispatch(
                                            storeActions.clearConfirmation()
                                        );
                                        saveCallback();
                                        resolve();
                                    },
                                    () => {
                                        store.customStore.dispatch(
                                            storeActions.clearConfirmation()
                                        );
                                        resolve();
                                    },
                                    null
                                )
                            );
                        });
                    });
                } else {
                    return new Promise<void>((resolve) => {
                        resolve();
                    });
                }
            }
        );
    };

    private static getSaveDataFromState = (
        props: ISettingsEmployeeDialogEditViewProp,
        state: ISettingsEmployeeDialogEditViewState
    ): SaveData => {
        const data = new SaveData();
        const employee = props.employeeEdit.employee;
        let oldDocuments = employee.documents.slice(0);
        for (let i = 0; i < employee.competencies.length; i++) {
            oldDocuments = oldDocuments.concat(
                employee.competencies[i].documents
            );
        }
        // Common
        data.append("id", employee.id);
        data.append("rowId", state.rowId);
        // General
        data.append("firstName", state.firstName);
        data.append("lastName", state.lastName);
        data.append("abbreviation", state.abbreviation);
        data.append("phone", state.phone);
        data.append("email", state.email);
        data.append("erp", state.erp);
        data.append("defaultCulture", state.defaultCulture);
        data.append("employeeGroupId", state.employeeGroupId);
        data.append("salaryEmployeeGroupId", state.salaryEmployeeGroupId);
        data.append(
            "vehicleId",
            state.vehicleId !== Base.emptyGuid ? state.vehicleId : String(null)
        );
        data.append(
            "costCenterId",
            state.costCenterId !== Base.emptyGuid
                ? state.costCenterId
                : String(null)
        );
        data.append("userGroupId", state.userGroupId);
        data.append("salaryTypeId", state.salaryTypeId);
        data.append("postalCodeRangeStart", state.postalCodeRangeStart);
        data.append("postalCodeRangeEnd", state.postalCodeRangeEnd);
        data.append("mapOnId", state.mapOnId.toString());
        data.append("tachoCardId", state.tachoCardId.replace(/\s/g, ""));
        if (employee.isNew()) {
            data.append("username", state.username);
            data.append("password", state.password);
        }
        data.append("activeState", state.activeState.toString(10));
        data.append(
            "workTimeAccountInUse",
            state.workTimeAccountInUse.toString()
        );
        if (!Base.isNullOrUndefined(state.pictureFile)) {
            data.append("picture[]", state.pictureFile, state.pictureFile.name);
        }
        data.append("removePicture", state.removePicture ? "1" : "0");
        // Competencies
        data.append(
            "competencies",
            JSON.stringify(
                state.competencies.reduce((result, i) => {
                    if (state.savedEmployeeCompetencyIds.indexOf(i.id) >= 0) {
                        result.push(
                            new EmployeeEditItemSaveEmployeeCompetencyItem(i)
                        );
                    }
                    return result;
                }, [])
            )
        );
        data.append(
            "removedEmployeeCompetencyIds",
            JSON.stringify(state.removedEmployeeCompetencyIds)
        );
        const documentUpdates: IDocumentInfoSave[] = [];
        const employeeCompetencyDocumentAdds: IDocumentInfoSave[] = [];
        const employeeCompetencyDocumentIds: string[] = [];
        for (let i = 0; i < state.competencies.length; i++) {
            const competency = state.competencies[i];
            if (state.savedEmployeeCompetencyIds.indexOf(competency.id) < 0)
                continue;
            for (let j = 0; j < competency.documents.length; j++) {
                const document = competency.documents[j];
                const file = document.file;
                const isOld = Base.isNullOrUndefined(file);
                if (isOld) {
                    const oldDocument = oldDocuments.find(
                        (k) => k.id === document.id
                    );
                    if (
                        !oldDocument ||
                        oldDocument.comment === document.comment
                    )
                        continue;
                    const documentSave = new DocumentInfoSave(document);
                    documentSave.relatedObjectId = competency.id;
                    documentUpdates.push(documentSave);
                } else {
                    data.append(
                        "employeeCompetencyDocuments[]",
                        file,
                        file.name
                    );
                    employeeCompetencyDocumentIds.push(competency.id);
                    const documentAdd = new DocumentInfoSave(document);
                    documentAdd.relatedObjectId = competency.id;
                    employeeCompetencyDocumentAdds.push(documentAdd);
                }
            }
        }
        data.append(
            "employeeCompetencyDocumentIds",
            JSON.stringify(employeeCompetencyDocumentIds)
        );
        data.append(
            "employeeCompetencyDocumentAdds",
            JSON.stringify(employeeCompetencyDocumentAdds)
        );
        // Documents
        const documentAdds: IDocumentInfoSave[] = [];
        for (let i = 0; i < state.documents.length; i++) {
            const document = state.documents[i];
            const file = document.file;
            const isOld = Base.isNullOrUndefined(file);
            if (isOld) {
                const oldDocument = oldDocuments.find(
                    (j) => j.id === document.id
                );
                if (!oldDocument || oldDocument.comment === document.comment)
                    continue;
                const documentSave = new DocumentInfoSave(document);
                documentSave.relatedObjectId = employee.id;
                documentUpdates.push(documentSave);
            } else {
                data.append("documents[]", file, file.name);
                const documentAdd = new DocumentInfoSave(document);
                documentAdd.relatedObjectId = employee.id;
                documentAdds.push(documentAdd);
            }
        }
        data.append(
            "removedDocumentIds",
            JSON.stringify(state.removedDocumentIds)
        );
        data.append("documentAdds", JSON.stringify(documentAdds));
        data.append("documentUpdates", JSON.stringify(documentUpdates));
        // SsoIntegrations
        data.append(
            "ssoIntegrations",
            JSON.stringify(
                props.ssoIntegrations.reduce((result, i) => {
                    result.push(new EmployeeEditItemSaveSsoIntegration(i));
                    return result;
                }, [])
            )
        );
        return data;
    };

    saveEmployee = (
        afterSaveCallback: (saved: boolean, employeeId: string) => void = null
    ) => {
        SettingsEmployeeDialogEditView.validate(this.props, this.state, () => {
            const saveData =
                SettingsEmployeeDialogEditView.getSaveDataFromState(
                    this.props,
                    this.state
                );
            if (Base.isNullOrUndefined(saveData)) return;
            // Call server
            store.customStore.dispatch(storeActions.fetchStart());
            employeeService
                .saveEmployeeEdit(saveData.formData)
                .then((success) => {
                    store.customStore.dispatch(
                        storeActions.showSuccessMessage(success.message)
                    );
                    tokenService.refreshAuthentication(true);
                    afterSaveCallback(true, success.id);
                })
                .catch((error) => {
                    store.customStore.dispatch(
                        storeActions.showErrorMessage(
                            baseService.getErrorMessageFromError(error)
                        )
                    );
                })
                .finally(() =>
                    store.customStore.dispatch(storeActions.fetchEnd())
                );
        });
    };

    handleChangePasswordDialogOk = (rowId: string) => {
        this.setState({
            rowId: rowId,
            showChangePasswordDialog: false,
        });
    };

    handleChangePasswordDialogCancel = () => {
        this.setState({
            showChangePasswordDialog: false,
        });
    };

    handleChangeUsernameDialogOk = (username: string, rowId: string) => {
        this.setState({
            rowId: rowId,
            username: username,
            showChangeUsernameDialog: false,
        });
    };

    handleChangeUsernameDialogCancel = () => {
        this.setState({
            showChangeUsernameDialog: false,
        });
    };

    private cancelEmployeeEditSub = (
        onCancelEditCallback: (saved: boolean, employeeId: string) => void
    ) => {
        const obj = this;
        const saveData = SettingsEmployeeDialogEditView.getSaveDataFromState(
            this.props,
            this.state
        );
        if (
            !Base.isNullOrUndefined(saveData) &&
            saveData.hash !== SettingsEmployeeDialogEditView.orgStateHash
        ) {
            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.warning"),
                    t("common.youHaveNotSavedChangesDoYouWantToSaveChanges"),
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                        obj.saveEmployee(onCancelEditCallback);
                    },
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                        onCancelEditCallback(
                            false,
                            obj.props.employeeEdit.employee.id
                        );
                    },
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                    }
                )
            );
        } else {
            onCancelEditCallback(false, obj.props.employeeEdit.employee.id);
        }
    };

    cancelEmployeeEdit = (
        onCancelEditCallback: (saved: boolean, employeeId: string) => void
    ) => {
        const obj = this;
        obj.cancelEmployeeEditSub(onCancelEditCallback);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const employeeEdit = this.props.employeeEdit;
        const employee = employeeEdit.employee;

        return (
            <div>
                <div className="row">
                    <div className="sideMenu col-3">
                        <div className="scrollSpyArea">
                            <ScrollSpy
                                dataContainer={this.employeeContentAreaDiv}
                                items={state.scrollSpyItems}
                                offset={0}
                            />
                        </div>
                    </div>
                    <div className="employeeContentArea employeeContainer col-9">
                        <div className="row">
                            <div id="general" className="col-3">
                                <ImageSelector
                                    fileInputName="picture"
                                    image={state.picture}
                                    aspectRatio={null}
                                    onChange={this.handleSetPicture}
                                    onClear={this.handleRemovePicture}
                                    onError={(message) => {
                                        store.customStore.dispatch(
                                            storeActions.showErrorMessage(
                                                message
                                            )
                                        );
                                    }}
                                />
                            </div>
                            <div className="col-9">
                                <div className="row">
                                    <div className="col-2">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.number")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="number"
                                                title={t("common.number")}
                                                value={
                                                    employee.isNew()
                                                        ? t("common.new")
                                                        : employee.number.toString(
                                                            10
                                                        )
                                                }
                                                readOnly={true}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.firstName")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="FNm"
                                                title={t("common.firstName")}
                                                value={state.firstName}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                                autoFocus={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.lastName")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="LNm"
                                                title={t("common.lastName")}
                                                value={state.lastName}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-2">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("common.abbreviation")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="abbreviation"
                                                title={t("common.abbreviation")}
                                                value={state.abbreviation}
                                                onChange={this.handleChange}
                                                maxLength={3}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.defaultCulture")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="defaultCulture"
                                                title={t("common.userGroup")}
                                                value={state.defaultCulture}
                                                onChange={this.handleChange}
                                            >
                                                <option
                                                    value={cultureCodeFinnish}
                                                >
                                                    {t(
                                                        "common.languageFinnish"
                                                    )}
                                                </option>
                                                <option
                                                    value={cultureCodeEnglish}
                                                >
                                                    {t(
                                                        "common.languageEnglish"
                                                    )}
                                                </option>
                                                <option
                                                    value={cultureCodeSwedish}
                                                >
                                                    {t(
                                                        "common.languageSwedish"
                                                    )}
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.userGroup")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="userGroupId"
                                                title={t("common.userGroup")}
                                                value={state.userGroupId}
                                                onChange={this.handleChange}
                                                disabled={
                                                    employeeEdit.editorIsEmployee
                                                }
                                            >
                                                {props.userGroups.map(
                                                    (userGroup) => (
                                                        <option
                                                            key={userGroup.id}
                                                            value={userGroup.id}
                                                        >
                                                            {userGroup.name}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.username")}
                                            </label>
                                            {employee.isNew() && (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="uNm"
                                                    title={t("common.username")}
                                                    value={state.username}
                                                    onChange={this.handleChange}
                                                    maxLength={50}
                                                />
                                            )}
                                            {!employee.isNew() && (
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        title={t(
                                                            "common.username"
                                                        )}
                                                        readOnly={true}
                                                        disabled={true}
                                                        value={state.username}
                                                    />
                                                    <span className="input-group-append">
                                                        <Button
                                                            title={t(
                                                                "common.change"
                                                            )}
                                                            classes={""}
                                                            enabled={true}
                                                            onClick={
                                                                this
                                                                    .handleChangeUsername
                                                            }
                                                        />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-3">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("common.password")}
                                            </label>
                                            {employee.isNew() && (
                                                <input
                                                    type="text"
                                                    className="form-control passwordFont"
                                                    name="password"
                                                    title={t("common.password")}
                                                    value={state.password}
                                                    onChange={this.handleChange}
                                                    maxLength={50}
                                                />
                                            )}
                                            {!employee.isNew() && (
                                                <div>
                                                    <Button
                                                        classes="btn-default changePassword"
                                                        title={t(
                                                            "common.changePassword"
                                                        )}
                                                        enabled={true}
                                                        onClick={(
                                                            index?: number
                                                        ) => {
                                                            this.handleChangePassword();
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {employee.isNew() && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="form-group required">
                                                <label className="control-label smallFont">
                                                    {t(
                                                        "common.passwordConfirmation"
                                                    )}
                                                </label>
                                                <input
                                                    type="text"
                                                    className={
                                                        "form-control passwordFont" +
                                                        (state.password !==
                                                        state.passwordConfirmation
                                                            ? " error"
                                                            : "")
                                                    }
                                                    name="passwordConfirmation"
                                                    title={t(
                                                        "common.passwordConfirmation"
                                                    )}
                                                    value={
                                                        state.passwordConfirmation
                                                    }
                                                    onChange={this.handleChange}
                                                    maxLength={50}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                &nbsp;
                                            </label>
                                            <div>
                                                <CheckBox
                                                    title={t("common.inUse")}
                                                    enabled={true}
                                                    checked={
                                                        state.activeState > 0
                                                    }
                                                    onCheckboxClickBoolean={(
                                                        checked: boolean
                                                    ) => {
                                                        this.handleActiveStateChange(
                                                            checked ? 1 : 0
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-9">
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("employee.forDesign")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="employeeGroupId"
                                                title={t("employee.forDesign")}
                                                value={state.employeeGroupId}
                                                onChange={this.handleChange}
                                            >
                                                {employeeEdit.employeeGroups.map(
                                                    (employeeGroup) => (
                                                        <option
                                                            key={
                                                                employeeGroup.id
                                                            }
                                                            value={
                                                                employeeGroup.id
                                                            }
                                                        >
                                                            {employeeGroup.name}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t(
                                                    "employee.forSalaryCalculation"
                                                )}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="salaryEmployeeGroupId"
                                                title={t(
                                                    "employee.forSalaryCalculation"
                                                )}
                                                value={
                                                    state.salaryEmployeeGroupId
                                                }
                                                onChange={this.handleChange}
                                            >
                                                {employeeEdit.employeeGroups
                                                    .filter(
                                                        (i) =>
                                                            !!i.salaryPeriodTypeId
                                                    )
                                                    .map((employeeGroup) => (
                                                        <option
                                                            key={
                                                                employeeGroup.id
                                                            }
                                                            value={
                                                                employeeGroup.id
                                                            }
                                                        >
                                                            {employeeGroup.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">
                                                {t("salary.type")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="salaryTypeId"
                                                title={t("salary.type")}
                                                value={state.salaryTypeId}
                                                onChange={this.handleChange}
                                            >
                                                {employeeEdit.salaryTypes.map(
                                                    (salaryType) => (
                                                        <option
                                                            key={salaryType.id}
                                                            value={
                                                                salaryType.id
                                                            }
                                                        >
                                                            {salaryType.title}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("common.phone")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="Phn"
                                                title={t("common.phone")}
                                                value={state.phone}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("common.email")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="Eml"
                                                title={t("common.email")}
                                                value={state.email}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("employee.maponId")}
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="maponid"
                                                title={t("employee.maponId")}
                                                value={state.mapOnId}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("employee.tachoCardId")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="tachoCardId"
                                                title={t(
                                                    "employee.tachoCardId"
                                                )}
                                                value={state.tachoCardId}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("vehicle.vehicle")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="vehicleId"
                                                title={t("vehicle.vehicle")}
                                                value={state.vehicleId}
                                                onChange={this.handleChange}
                                            >
                                                {state.vehicles.map(
                                                    (vehicle) => (
                                                        <option
                                                            key={vehicle.id}
                                                            value={vehicle.id}
                                                        >
                                                            {vehicle.getTitle()}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("common.erpReference")}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="erp"
                                                title={t("common.erpReference")}
                                                value={state.erp}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t("costCenter.costCenter")}
                                            </label>
                                            <select
                                                className="form-control"
                                                name="costCenterId"
                                                title={t(
                                                    "costCenter.costCenter"
                                                )}
                                                value={state.costCenterId}
                                                onChange={this.handleChange}
                                            >
                                                {state.costCenters.map(
                                                    (costCenter) => (
                                                        <option
                                                            key={costCenter.id}
                                                            value={
                                                                costCenter.id
                                                            }
                                                        >
                                                            {costCenter.title}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                &nbsp;
                                            </label>
                                            <div>
                                                <CheckBox
                                                    title={t(
                                                        "common.workhourBankInUse"
                                                    )}
                                                    enabled={true}
                                                    checked={
                                                        state.workTimeAccountInUse
                                                    }
                                                    onCheckboxClickBoolean={
                                                        this
                                                            .handleWorkTimeInUseChange
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t(
                                                    "common.workQueuePostalCodeRangeStart"
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="PostalCodeRangeFrom"
                                                title={t(
                                                    "common.workQueuePostalCodeRangeStart"
                                                )}
                                                value={
                                                    state.postalCodeRangeStart
                                                }
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label className="control-label smallFont">
                                                {t(
                                                    "common.workQueuePostalCodeRangeEnd"
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="PostalCodeRangeTo"
                                                title={t(
                                                    "common.workQueuePostalCodeRangeEnd"
                                                )}
                                                value={state.postalCodeRangeEnd}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div id="employeeGroups">
                            <SettingsEmployeeDialogEmployeeGroupList
                                title={t("common.secondaryEmployeeGroups")}
                                employeeId={employee.id}
                                reservedEmployeeGroupIds={[
                                    state.employeeGroupId,
                                    state.salaryEmployeeGroupId,
                                ]}
                                readOnly={employee.isNew()}
                                items={props.employeeEmployeeGroups}
                                onRemoveEmployeeGroup={
                                    props.onEmployeeEmployeeGroupRemoved
                                }
                                onAddEmployeeGroup={
                                    props.onAddEmployeeEmployeeGroup
                                }
                            />
                        </div>
                        <div id="dayBookings">
                            <SettingsEmployeeDialogDayBookingList
                                title={t("dayBooking.s")}
                                isReadOnly={employee.isNew()}
                                employeeId={employee.id}
                                dayBookingTypes={employeeEdit.dayBookingTypes}
                                items={props.dayBookings}
                                onDayBookingRemoved={props.onDayBookingRemoved}
                                onAddDayBooking={props.onAddDayBooking}
                                onEditDayBooking={props.onEditDayBooking}
                            />
                        </div>
                        {state.workTimeAccountInUse && (
                            <div id="workHourTransactions">
                                <SettingsEmployeeWorkHourAccountTransactions
                                    title={
                                        t("common.workhourBank") +
                                        ", " +
                                        t("common.workHourBalance") +
                                        " " +
                                        props.workHourBalance
                                    }
                                    items={props.workHourTransactions}
                                />
                            </div>
                        )}
                        <div id="competencies">
                            <SettingsEmployeeDialogEmployeeCompetencyList
                                title={t("common.competencies")}
                                competencies={employeeEdit.competencies}
                                items={state.competencies}
                                competencyWarningLimitInDays={
                                    employeeEdit.competencyWarningLimitInDays
                                }
                                onRemoveEmployeeCompetency={
                                    this.handleEmployeeCompetencyRemove
                                }
                                onAddEmployeeCompetency={
                                    this.handleEmployeeCompetencyAdd
                                }
                                onEditEmployeeCompetency={
                                    this.handleEmployeeCompetencyEdit
                                }
                            />
                        </div>
                        <div id="documents">
                            <DocumentList
                                title={t("document.s")}
                                relatedObjectId={employee.id}
                                documents={state.documents}
                                isReadOnly={false}
                                canShowCamera={false}
                                changeDocumentTypeEnabled={true}
                                onEditDocument={this.handleDocumentEdit}
                                onRemoveDocument={this.handleDocumentRemove}
                                onDownloadDocument={this.handleDocumentDownload}
                                onAddDocuments={this.handleDocumentsAdd}
                                onAddPhoto={null}
                            />
                        </div>
                        <div id="integrations">
                            <SettingsEmployeeDialogSsoIntegrationList
                                title={t("common.ssoIntegrations")}
                                isReadOnly={employee.isNew()}
                                items={props.ssoIntegrations}
                                onEditSsoIntegration={
                                    props.onEditSsoIntegration
                                }
                            />
                        </div>
                    </div>
                </div>
                {state.showChangePasswordDialog && (
                    <SettingsChangePasswordDialog
                        employeeId={employee.id}
                        employeeRowId={state.rowId}
                        adminMode={true}
                        onOk={this.handleChangePasswordDialogOk}
                        onCancel={this.handleChangePasswordDialogCancel}
                    />
                )}
                {state.showChangeUsernameDialog && (
                    <SettingsEmployeeDialogChangeUsernameDialog
                        employeeId={employee.id}
                        employeeRowId={state.rowId}
                        username={employee.username}
                        onOk={this.handleChangeUsernameDialogOk}
                        onCancel={this.handleChangeUsernameDialogCancel}
                    />
                )}
            </div>
        );
    }
}
